import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { MIME_TYPES, nextAnimationFrame } from "@excalidraw/common";
import { duplicateElements } from "@excalidraw/element";
import clsx from "clsx";

import { deburr } from "../deburr";
import type { ExcalidrawLibraryIds } from "../data/types";
import { useLibraryCache } from "../hooks/useLibraryItemSvg";
import { useScrollPosition } from "../hooks/useScrollPosition";
import { t } from "../i18n";
import type {
  ExcalidrawProps,
  LibraryItem,
  LibraryItems,
  UIAppState,
} from "../types";

import { useDevice } from "./App";
import { Button } from "./Button";
import { createIcon } from "./icons";
import {
  LibraryMenuSection,
  LibraryMenuSectionGrid,
} from "./LibraryMenuSection";
import Spinner from "./Spinner";
import Stack from "./Stack";
import { TextField } from "./TextField";

import "./LibraryMenuItems.scss";

const ITEMS_RENDERED_PER_BATCH = 17;
const CACHED_ITEMS_RENDERED_PER_BATCH = 64;

const SearchIcon = createIcon(
  <g
    strokeWidth="1.5"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </g>,
  { width: 24, height: 24 },
);

const LANGUAGE_OPTIONS = ["fr", "en", "es", "de", "it", "pt", "ar"];

const detectInitialLanguage = () => {
  if (typeof window === "undefined") {
    return "en";
  }
  const candidate =
    localStorage.getItem("i18nextLng") ||
    localStorage.getItem("excalidraw-lang") ||
    navigator.language ||
    "en";
  const normalized = candidate.split("-")[0]?.toLowerCase() || "en";
  return LANGUAGE_OPTIONS.includes(normalized) ? normalized : "en";
};

export default function LibraryMenuItems({
  isLoading,
  libraryItems,
  onInsertLibraryItems,
  pendingElements,
  theme,
  id,
  libraryReturnUrl,
  onSelectItems,
  selectedItems,
}: {
  isLoading: boolean;
  libraryItems: LibraryItems;
  pendingElements: LibraryItem["elements"];
  onInsertLibraryItems: (libraryItems: LibraryItems) => void;
  onAddToLibrary: (elements: LibraryItem["elements"]) => void;
  libraryReturnUrl: ExcalidrawProps["libraryReturnUrl"];
  theme: UIAppState["theme"];
  id: string;
  selectedItems: LibraryItem["id"][];
  onSelectItems: (id: LibraryItem["id"][]) => void;
}) {
  const device = useDevice();
  const libraryContainerRef = useRef<HTMLDivElement>(null);
  const scrollPosition = useScrollPosition<HTMLDivElement>(libraryContainerRef);
  const { svgCache } = useLibraryCache();
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] =
    useState(detectInitialLanguage);

  void pendingElements;
  void theme;
  void id;
  void libraryReturnUrl;
  void onSelectItems;
  void selectedItems;

  useEffect(() => {
    if (scrollPosition > 0) {
      libraryContainerRef.current?.scrollTo(0, scrollPosition);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredItems = useMemo(() => {
    const searchQuery = deburr(searchInputValue.trim().toLowerCase());
    return libraryItems.filter((item) => {
      const lang = item.language?.trim().toLowerCase();
      if (selectedLanguage && lang !== selectedLanguage) {
        return false;
      }
      if (!searchQuery) {
        return true;
      }

      const itemName = item.name || "";
      if (!itemName.trim()) {
        return false;
      }
      const normalizedName = deburr(itemName.toLowerCase());
      if (normalizedName.includes(searchQuery)) {
        return true;
      }
      const words = normalizedName.split(/[\s-_]+/);
      return words.some((word) => word.startsWith(searchQuery));
    });
  }, [libraryItems, searchInputValue, selectedLanguage]);

  const categorizedItems = useMemo(() => {
    const categorized = new Map<string, LibraryItem[]>();
    const uncategorized: LibraryItem[] = [];

    filteredItems.forEach((item) => {
      const category = item.category?.trim();
      if (!category) {
        uncategorized.push(item);
        return;
      }
      const items = categorized.get(category) || [];
      items.push(item);
      categorized.set(category, items);
    });

    const entries = [...categorized.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, items]) => [
        category,
        [...items].sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", undefined, {
            sensitivity: "base",
          }),
        ),
      ] as const);

    if (uncategorized.length > 0) {
      entries.push([
        "Non catégorisé",
        [...uncategorized].sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", undefined, {
            sensitivity: "base",
          }),
        ),
      ]);
    }

    return entries;
  }, [filteredItems]);

  const getInsertedElements = useCallback(
    (id: string) => {
      return libraryItems
        .filter((item) => item.id === id)
        .map((item) => ({
          ...item,
          elements: duplicateElements({
            type: "everything",
            elements: item.elements,
            randomizeSeed: true,
          }).duplicatedElements,
        }));
    },
    [libraryItems],
  );

  const onItemDrag = useCallback((id: LibraryItem["id"], event: React.DragEvent) => {
    const data: ExcalidrawLibraryIds = { itemIds: [id] };
    event.dataTransfer.setData(
      MIME_TYPES.excalidrawlibIds,
      JSON.stringify(data),
    );
  }, []);

  const onItemClick = useCallback(
    (id: LibraryItem["id"] | null) => {
      if (id) {
        onInsertLibraryItems(getInsertedElements(id));
      }
    },
    [getInsertedElements, onInsertLibraryItems],
  );

  const itemsRenderedPerBatch =
    svgCache.size >= filteredItems.length
      ? CACHED_ITEMS_RENDERED_PER_BATCH
      : ITEMS_RENDERED_PER_BATCH;

  const searchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isSearchOpen) {
      nextAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
    }
  }, [isSearchOpen]);

  const isLibraryEmpty = !filteredItems.length;

  return (
    <div
      className="library-menu-items-container"
      style={filteredItems.length ? { justifyContent: "flex-start" } : { borderBottom: 0 }}
    >
      <div
        className="library-menu-items-header"
        style={{ flexDirection: "column", alignItems: "stretch" }}
      >
        <div
          style={{
            display: "flex",
            width: "100%",
            gap: "0.5rem",
            alignItems: "center",
          }}
        >
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            className="library-filter-select"
            style={{
              flex: 1,
              padding: "0.5rem",
              borderRadius: "var(--border-radius-lg)",
              border: "1px solid var(--sidebar-border-color)",
              fontSize: "0.8rem",
              background: "var(--input-bg-color)",
              color: "var(--text-primary-color)",
            }}
          >
            {LANGUAGE_OPTIONS.map((lang) => (
              <option key={lang} value={lang}>
                {lang.toUpperCase()}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="library-search-toggle"
            onClick={() => {
              setIsSearchOpen((value) => !value);
              if (isSearchOpen) {
                setSearchInputValue("");
              }
            }}
            aria-label={t("library.search.inputPlaceholder")}
            title={t("library.search.inputPlaceholder")}
          >
            {SearchIcon}
          </button>
        </div>

        {isSearchOpen && (
          <div style={{ marginTop: "0.5rem" }}>
            <TextField
              ref={searchInputRef}
              type="search"
              className={clsx("library-menu-items-container__search", {
                hideCancelButton: !device.editor.isMobile,
              })}
              placeholder={t("library.search.inputPlaceholder")}
              value={searchInputValue}
              onChange={(value) => setSearchInputValue(value)}
            />
          </div>
        )}
      </div>

      <Stack.Col
        className="library-menu-items-container__items"
        align="start"
        gap={1}
        style={{
          flex: filteredItems.length > 0 ? 1 : "0 1 auto",
          margin: isLibraryEmpty ? "auto" : 0,
        }}
        ref={libraryContainerRef}
      >
        {isLoading ? (
          <div className="layer-ui__library-message">
            <div>
              <Spinner size="2em" />
              <span>{t("labels.libraryLoadingMessage")}</span>
            </div>
          </div>
        ) : isLibraryEmpty ? (
          <div className="library-menu-items__no-items">
            <div className="library-menu-items__no-items__label">
              {t("library.noItems")}
            </div>
            <div className="library-menu-items__no-items__hint">
              {searchInputValue.trim()
                ? t("library.search.noResults")
                : "Aucun template disponible pour cette langue."}
            </div>
            {searchInputValue.trim() ? (
              <Button
                onPointerDown={(e) => e.preventDefault()}
                onSelect={() => {
                  setSearchInputValue("");
                }}
                style={{ width: "auto", marginTop: "1rem" }}
              >
                {t("library.search.clearSearch")}
              </Button>
            ) : null}
          </div>
        ) : (
          categorizedItems.map(([category, items]) => (
            <React.Fragment key={category}>
              <div
                className="library-menu-items-container__header"
                style={{ marginTop: "0.75rem" }}
              >
                {category}
              </div>
              <LibraryMenuSectionGrid>
                <LibraryMenuSection
                  itemsRenderedPerBatch={itemsRenderedPerBatch}
                  items={items}
                  onItemSelectToggle={() => undefined}
                  onItemDrag={onItemDrag}
                  onClick={onItemClick}
                  isItemSelected={() => false}
                  svgCache={svgCache}
                  showSelection={false}
                />
              </LibraryMenuSectionGrid>
            </React.Fragment>
          ))
        )}
      </Stack.Col>
    </div>
  );
}
